import React, { useEffect, useMemo, useState } from 'react';
import styles from './DPViews.module.css';
import { styled } from "@mui/system";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import vector from "../../../../../assets/images/Vector.svg";
import { colors } from 'Samarth Module/constants/dailyPhasing';
import Pagination from 'Samarth Module/components/Pagination/Pagination';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        color: "#fff",
        textAlign: "center",
        padding: "5px",
        fontSize: "12px",
        fontWeight: 600,
        outline: "1px solid #EAECF0",
        whiteSpace: "nowrap",
    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: "12px",
        fontWeight: 400,
        padding: "5px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
        textAlign: "center",
        whiteSpace: "nowrap",
    },
}));

const StyledTableRow = styled(TableRow)({
    backgroundColor: "#FFFFFF !important"
});

function DPScheduleView({ tableData, columns }) {
    const [localTableData, setLocalTableData] = useState(tableData);
    const [hover, setHover] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
    const [cbuInfo, setCbuinfo] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const selectedColumns = columns.filter((a) => a.visibility === true);

    useEffect(() => {
        const tdata = [...tableData];
        setLocalTableData([...tdata]);
        const cbuDetails = [];
        const uniqueCBUCodes = new Set();
        let count = 0;
        for (const entry of [...tdata]) {
            for (const day of ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']) {
                const cbuCode = entry[day] ? entry[day]['CBU code'] : "";
                const cbuDesc = entry[day] ? entry[day]['CBU Description'] : "";

                if (!uniqueCBUCodes.has(cbuCode)) {
                    uniqueCBUCodes.add(cbuCode);
                    cbuDetails.push({
                        'CBU code': cbuCode,
                        'CBU Description': cbuDesc,
                        'background': colors[count]
                    });
                    count++;
                }
            }
        }
        cbuDetails.push({ 'CBU Description': 'Planned holiday', "background": '#E5E4E2' })
        cbuDetails.push({ 'CBU Description': 'Planned maintanence', "background": '#949495' })
        cbuDetails.push({ 'CBU Description': 'No plan', "background": '#FFFFFF' });
        setCbuinfo(cbuDetails);
        setPage(0);
    }, [tableData]);

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const sortedData = useMemo(() => {
        if (sortConfig.key) {
            return [...localTableData].sort((a, b) => {
                const valueA = a[sortConfig.key];
                const valueB = b[sortConfig.key];
                if (!isNaN(valueA) && !isNaN(valueB)) {
                    return sortConfig.direction === "asc" ? valueA - valueB : valueB - valueA;
                }
                if (typeof valueA === "string" && typeof valueB === "string") {
                    return sortConfig.direction === "asc"
                        ? valueA.localeCompare(valueB)
                        : valueB.localeCompare(valueA);
                }
                return 0;
            });
        }
        return localTableData;
    }, [localTableData, sortConfig]);

    const rowTotal = (row) => {
        let total = 0;
        Object.keys(row).forEach((item) => {
            if (typeof row[item] === "object") {
                total += typeof row[item]["A"] !== 'string' ? row[item]["A"] : 0
                    + typeof row[item]["B"] !== 'string' ? row[item]["B"] : 0
                        + typeof row[item]["C"] !== 'string' ? row[item]["C"] : 0;
            }
        });

        return total.toFixed(2);
    };

    const checkingTitle = (data) => {
        if (data === 'PM') {
            return 'Planned Maintainance'
        } else if (data === 'PH') {
            return 'Planned Holiday'
        }
    }

    const colSpan = (col) => {
        return col.endsWith('day') ? 3 : 1;
    }

    const identifyCellBgColor = (item, shift) => {
        let color;
        for (let cbu of [...cbuInfo]) {
            if (cbu['CBU code'] === item['CBU code']) {
                color = item[shift] === 'PM' ? '#949495' : item[shift] === 'PH' ? '#E5E4E2' : cbu['background'];
            }
        }
        return color;
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            {/* schedule view table */}
            <TableContainer sx={{ maxHeight: "45vh" }}>
                <Table>
                    <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1000 }}>
                        {selectedColumns.length ? (
                            <>
                                <TableRow>
                                    {selectedColumns.map((col, index) => (
                                        <StyledTableCell
                                            key={index}
                                            colSpan={colSpan(col.label)}
                                            onClick={() => handleSort(col.key)}
                                            onMouseEnter={() => setHover((prev) => ({ ...prev, [col.key]: true }))}
                                            onMouseLeave={() => setHover((prev) => ({ ...prev, [col.key]: false }))}
                                            sx={{ cursor: col.label.endsWith('day') ? "auto" : "pointer", backgroundColor: "var(--primany-table-header-color)" }}
                                        >
                                            <div className={styles.sortIcon}>
                                                {col.label}
                                                {col.sortable && (
                                                    <img
                                                        src={vector}
                                                        alt="sort icon"
                                                        width="14px"
                                                        height="14px"
                                                        style={{
                                                            visibility: hover[col.key] ? "visible" : "hidden",
                                                            display: hover[col.key] ? 'block' : 'none'
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </StyledTableCell>
                                    ))}
                                    <StyledTableCell
                                        colSpan={2}
                                        sx={{ backgroundColor: "var(--primany-table-header-color)" }}
                                    >
                                        Total
                                    </StyledTableCell>
                                </TableRow>

                                <TableRow>
                                    <StyledTableCell
                                        colSpan={2}
                                        className={styles.otherSubCols}
                                    >
                                        Shift timings
                                    </StyledTableCell>
                                    {Array(7).fill(0).map((col, idx) => (
                                        <React.Fragment key={`_${idx + 1}_`}>
                                            <StyledTableCell className={styles.subCols}>A</StyledTableCell>
                                            <StyledTableCell className={styles.subCols}>B</StyledTableCell>
                                            <StyledTableCell className={styles.subCols}>C</StyledTableCell>
                                        </React.Fragment>
                                    ))}
                                    <StyledTableCell className={styles.otherSubCols}>
                                        (in tonnes)
                                    </StyledTableCell>
                                </TableRow>
                            </>
                        ) : (
                            <TableRow>
                                <StyledTableCell sx={{ backgroundColor: "var(--primany-table-header-color)" }}>
                                    No columns available...
                                </StyledTableCell>
                            </TableRow>
                        )
                        }
                    </TableHead>
                    <TableBody>
                        {sortedData.length > 0 ? (
                            sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                                <StyledTableRow key={`${idx + 2}}`}>
                                    {selectedColumns.map((col, colIdx) =>
                                        typeof row[col.label] === "object" ? (
                                            <React.Fragment key={`${idx + colIdx}`}>
                                                <StyledTableCell sx={{ backgroundColor: identifyCellBgColor(row[col.label], 'A') }} title={(typeof row[col.label]["A"] == 'string' && row[col.label]["A"] !== 'nan') ? checkingTitle(row[col.label]["A"]) : ''}>
                                                    {row[col.label]["A"] === 'nan' ? 0 : row[col.label]["A"] === 'PM' || row[col.label]["A"] === 'PH' ? '' : row[col.label]["A"]}
                                                </StyledTableCell>
                                                <StyledTableCell sx={{ backgroundColor: identifyCellBgColor(row[col.label], 'B') }} title={(typeof row[col.label]["B"] == 'string' && row[col.label]["B"] !== 'nan') ? checkingTitle(row[col.label]["B"]) : ''}>
                                                    {row[col.label]["B"] === 'nan' ? 0 : row[col.label]["B"] === 'PM' || row[col.label]["B"] === 'PH' ? '' : row[col.label]["B"]}
                                                </StyledTableCell>
                                                <StyledTableCell sx={{ backgroundColor: identifyCellBgColor(row[col.label], 'C') }} title={(typeof row[col.label]["C"] == 'string' && row[col.label]["C"] !== 'nan') ? checkingTitle(row[col.label]["C"]) : ''}>
                                                    {row[col.label]["C"] === 'nan' ? 0 : row[col.label]["C"] === 'PM' || row[col.label]["C"] === 'PH' ? '' : row[col.label]["C"]}
                                                </StyledTableCell>

                                            </React.Fragment>
                                        ) : (
                                            <StyledTableCell key={`${idx + colIdx}`}>{row[col.label]}</StyledTableCell>
                                        )
                                    )}
                                    <StyledTableCell className={styles.totalCol}>
                                        {rowTotal(row)}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={10} sx={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                                    Data not available...
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Pagination page={page} tableData={sortedData} dataPerPage={rowsPerPage} handleChangePage={(event, pageno) => handleChangePage(event, pageno)} handleChangeRowsPerPage={(pageno) => handleChangeRowsPerPage(pageno)} />

            {/* showing the list of the cbu & cbu descriptions */}
            <div className={styles.cbuListContainer}>
                {Boolean(localTableData.length) && cbuInfo.map((item, index) => (
                    <div key={index + Math.random()} className={`${styles.cbuDescList} ${item['CBU code'] ? '' : styles.centerAlignment}`}>
                        <div className={styles.colorForCBU} style={{ background: item['background'] ? item['background'] : '' }}></div>
                        <div>
                            {item['CBU code'] && <p className={styles.cbu_code}>{item['CBU code']}&nbsp;:</p>}
                            <p className={styles.cbu_description}>{item['CBU Description']}</p>
                        </div>
                    </div>
                ))
                }
            </div>
        </>
    )
}

export default DPScheduleView;